import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Box,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  Logout,
  Settings,
  Help,
  DocumentScanner,
  MedicalInformation,
  Quiz,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import routes from '../config/routes';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import { useSnackbar } from './SnackbarProvider';

export default function MobilePatientHeader({ patient, notifications = 0 }) {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { logout: authLogout } = useEnterpriseAuth();
  const { showSnackbar } = useSnackbar();

  const handleLogout = async () => {
    try {
      // Clear patient-specific data
      localStorage.removeItem('patientToken');
      localStorage.removeItem('patientData');
      localStorage.removeItem('patientMessages');
      
      // Use the EnterpriseAuth logout which handles all auth cleanup
      await authLogout();
      
      showSnackbar('You have been logged out successfully.', 'info');
      navigate(routes.login());
    } catch (error) {
      console.error('[MobilePatientHeader] Logout error:', error);
      // Even if logout fails, clear local data and redirect
      localStorage.clear();
      sessionStorage.clear();
      navigate(routes.login());
    }
  };

  const menuItems = [
    { 
      icon: <MedicalInformation />, 
      text: 'Medical History', 
      onClick: () => navigate('/patient/medical-history') 
    },
    { 
      icon: <DocumentScanner />, 
      text: 'Documents', 
      onClick: () => navigate('/patient/documents') 
    },
    { 
      icon: <Quiz />, 
      text: 'Questionnaires', 
      onClick: () => navigate('/patient/questionnaires') 
    },
    { 
      icon: <Settings />, 
      text: 'Settings', 
      onClick: () => navigate('/patient/settings') 
    },
    { 
      icon: <Help />, 
      text: 'Help & Support', 
      onClick: () => navigate('/patient/help') 
    },
  ];

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          display: { xs: 'block', md: 'none' },
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
        elevation={1}
      >
        <Toolbar sx={{ minHeight: { xs: 56 } }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            VirtualPAT
          </Typography>

          <IconButton color="inherit" sx={{ mr: 1 }}>
            <Badge badgeContent={notifications} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          <Avatar
            sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
            onClick={() => navigate('/patient/profile')}
          >
            {patient?.firstName?.[0] || 'P'}
          </Avatar>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            {patient?.firstName?.[0] || 'P'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {patient?.firstName} {patient?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {patient?.email}
            </Typography>
          </Box>
        </Box>
        
        <Divider />
        
        <List>
          {menuItems.map((item, index) => (
            <ListItemButton
              key={index}
              onClick={() => {
                item.onClick();
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          ))}
        </List>
        
        <Divider />
        
        <List>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <Logout color="error" />
            </ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ color: 'error' }} />
          </ListItemButton>
        </List>
      </Drawer>

      {/* Toolbar spacer */}
      <Toolbar sx={{ display: { xs: 'block', md: 'none' } }} />
    </>
  );
}
